import { useEffect, useMemo, useState } from "react";
import { GraduationCap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState } from "@/components/States";
import { api, formatError } from "@/lib/api";
import { awardedDate, ResendEmailButton } from "@/pages/Badges";

const todayStr = () => new Date().toISOString().slice(0, 10);

function IssueForm({ onIssued }) {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ user_id: "", course_id: "", completion_date: todayStr() });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.active !== false)));
    api.get("/admin/courses").then((r) => setCourses(r.data));
  }, []);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return users.filter((u) => !s || u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s)).slice(0, 50);
  }, [users, q]);
  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/course-certificates", f);
      toast.success(data.email_status === "sent" ? "Certificate issued and emailed to the learner" : "Certificate issued (email could not be sent)");
      setF({ ...f, user_id: "", course_id: "" }); onIssued();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="cert-issue-form" className="rounded-2xl border border-line bg-surface p-6">
      <h3 className="flex items-center gap-2 font-bold text-ink"><GraduationCap className="h-5 w-5 text-aqua" />Issue a course completion certificate</h3>
      <p className="mt-1 text-sm text-ink-3">The learner sees it on their Badges page and gets it by email as a PDF with a QR code for verification.</p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Input data-testid="cert-user-search" placeholder="Search learner by name or email" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
            <SelectTrigger data-testid="cert-user-select"><SelectValue placeholder="Select learner" /></SelectTrigger>
            <SelectContent>{shown.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2">
          <Select value={f.course_id} onValueChange={(v) => setF({ ...f, course_id: v })}>
            <SelectTrigger data-testid="cert-course-select"><SelectValue placeholder="Select IDDCR course" /></SelectTrigger>
            <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
          </Select>
          <label className="text-xs text-ink-3">Completion date
            <Input data-testid="cert-date-input" type="date" max={todayStr()} value={f.completion_date} onChange={(e) => setF({ ...f, completion_date: e.target.value })} className="mt-1" />
          </label>
        </div>
      </div>
      <Button data-testid="cert-issue-btn" disabled={busy || !f.user_id || !f.course_id || !f.completion_date} onClick={issue} className="mt-4 rounded-full bg-solid">
        {busy ? "Issuing..." : "Issue certificate"}
      </Button>
    </div>
  );
}

export default function AdminCertificates() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/course-certificates").then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="flex flex-col gap-6">
      <IssueForm onIssued={load} />
      {!items ? <Loading /> : items.length === 0 ? <EmptyState testId="cert-empty" title="No certificates yet" text="Course certificates you issue and mastery certificates learners earn will appear here." /> : (
        <div data-testid="cert-list" className="flex flex-col gap-3">
          {items.map((b) => (
            <div key={b.id} data-testid={`cert-item-${b.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface p-4">
              <div>
                <p className="font-semibold text-ink">{b.title} <span className="ml-1 rounded-full bg-elevated px-2 py-0.5 text-[11px] font-medium capitalize text-ink-2">{b.kind}</span></p>
                <p className="text-sm text-ink-2">{b.user_name} · {b.kind === "course" ? "completed" : "awarded"} {awardedDate(b.awarded_at).toLocaleDateString()}</p>
                <p className="text-xs text-ink-3">{b.issued_at ? `Issued ${new Date(b.issued_at).toLocaleString()} by ${b.issued_by} · ` : "Earned in app · "}email {b.email_status || "not sent"} · {b.views || 0} verification views</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
              <ResendEmailButton path={`/admin/badges/${b.id}/resend-email`} status={b.email_status} testId={`cert-resend-${b.id}`} onDone={load} />
              <Button asChild size="sm" variant="outline" className="rounded-full">
                <a data-testid={`cert-verify-link-${b.id}`} href={`/badge/${b.id}`} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Verification page</a>
              </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
